import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { SocketProvider } from './contexts/SocketContext';
import Layout from './components/Layout/Layout';
import DashboardLayout from './components/Layout/DashboardLayout';
import ProtectedRoute from './components/Auth/ProtectedRoute';
import Home from './pages/Home';
import Login from './pages/Login';
import Register from './pages/Register';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import VerifyEmail from './pages/VerifyEmail';
import Dashboard from './pages/Dashboard';
import Monitors from './pages/Monitors';
import MonitorDetails from './pages/MonitorDetails';
import Settings from './pages/Settings';
import keepAliveService from './services/keepAliveService';

// Component to handle authenticated user redirects
const AuthenticatedRedirect = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) return null;

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

function App() {
  // Start keep-alive service when app mounts
  useEffect(() => {
    keepAliveService.start();

    // Cleanup on unmount
    return () => {
      keepAliveService.stop();
    };
  }, []);

  return (
    <AuthProvider>
      <SocketProvider>
        <Router>
          <Layout>
            <Routes>
              {/* Public routes */}
              <Route
                path="/"
                element={
                  <AuthenticatedRedirect>
                    <Home />
                  </AuthenticatedRedirect>
                }
              />
              <Route
                path="/login"
                element={
                  <AuthenticatedRedirect>
                    <Login />
                  </AuthenticatedRedirect>
                }
              />
              <Route
                path="/register"
                element={
                  <AuthenticatedRedirect>
                    <Register />
                  </AuthenticatedRedirect>
                }
              />
              <Route
                path="/forgot-password"
                element={
                  <AuthenticatedRedirect>
                    <ForgotPassword />
                  </AuthenticatedRedirect>
                }
              />
              <Route
                path="/reset-password/:token"
                element={
                  <AuthenticatedRedirect>
                    <ResetPassword />
                  </AuthenticatedRedirect>
                }
              />
              <Route
                path="/verify-email/:token"
                element={
                  <AuthenticatedRedirect>
                    <VerifyEmail />
                  </AuthenticatedRedirect>
                }
              />

              {/* Protected routes with Dashboard Layout */}
              <Route
                path="/dashboard"
                element={
                  <ProtectedRoute>
                    <DashboardLayout>
                      <Dashboard />
                    </DashboardLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/monitors"
                element={
                  <ProtectedRoute>
                    <DashboardLayout>
                      <Monitors />
                    </DashboardLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/settings"
                element={
                  <ProtectedRoute>
                    <DashboardLayout>
                      <Settings />
                    </DashboardLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/monitor/:id"
                element={
                  <ProtectedRoute>
                    <DashboardLayout>
                      <MonitorDetails />
                    </DashboardLayout>
                  </ProtectedRoute>
                }
              />

              {/* Catch all route */}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </Layout>
        </Router>
      </SocketProvider>
    </AuthProvider>
  );
}

export default App;
